import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/site/Logo";
import { CONTACT } from "@/data/site";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const links = [
  { label: "Prestations", id: "services" },
  { label: "Le véhicule", id: "vehicle" },
  { label: "Approche", id: "manifesto" },
  { label: "Contact", id: "booking" },
];

const marquee = "DISPONIBLE 7J/7 — RÉSERVATION IMMÉDIATE — TRANSFERTS AÉROPORT — MISE À DISPOSITION — VÉHICULE MARIAGE — RÉGION LYONNAISE — ";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hover, setHover] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Announcement bar */}
      <div
        className={`fixed top-0 left-0 right-0 z-[60] bg-[#0B132B] border-b border-white/10 overflow-hidden transition-[height,opacity] duration-500 ${
          scrolled ? "h-0 opacity-0" : "h-9 opacity-100"
        }`}
      >
        <div className="marquee-track h-9 items-center flex">
          {[0, 1].map((k) => (
            <span key={k} className="text-[10px] tracking-[0.3em] text-white/55 font-medium px-4 flex items-center">
              {marquee}
            </span>
          ))}
        </div>
      </div>

      <header
        data-testid="navbar"
        className={`fixed left-0 right-0 z-50 transition-[top] duration-500 ${scrolled ? "top-4" : "top-9"}`}
      >
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div
            className={`flex items-center justify-between transition-[padding,background-color,border-color,box-shadow,border-radius] duration-500 ${
              scrolled
                ? "bg-[#0B132B]/70 backdrop-blur-2xl border border-white/10 rounded-full px-4 py-2.5 shadow-2xl shadow-black/30 mt-2"
                : "bg-transparent border border-transparent px-2 py-4"
            }`}
          >
            <Logo onClick={() => scrollTo("top")} compact={scrolled} />

            {/* Center capsule nav */}
            <nav
              onMouseLeave={() => setHover(null)}
              className="hidden lg:flex items-center gap-1 bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-full p-1.5"
            >
              {links.map((l, i) => (
                <button
                  key={l.id}
                  data-testid={`nav-link-${l.id}`}
                  onMouseEnter={() => setHover(i)}
                  onClick={() => scrollTo(l.id)}
                  className="relative px-5 py-2 text-sm text-white/75 hover:text-white transition-colors duration-200"
                >
                  {hover === i && (
                    <motion.span
                      layoutId="nav-pill"
                      className="absolute inset-0 bg-white/12 rounded-full"
                      transition={{ type: "spring", stiffness: 420, damping: 34 }}
                    />
                  )}
                  <span className="relative z-10">{l.label}</span>
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-2 md:gap-3">
              <a
                data-testid="nav-call-btn"
                href={`tel:${CONTACT.phoneIntl}`}
                className="hidden sm:flex items-center gap-2 text-white/85 hover:text-white text-sm font-medium px-3 py-2 transition-colors duration-300"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                {CONTACT.phoneDisplay}
              </a>

              <button
                data-testid="nav-reserve-btn"
                onClick={() => scrollTo("booking")}
                className="shine-btn group hidden md:flex items-center gap-2 bg-white text-[#0B132B] pl-5 pr-4 py-2.5 rounded-full text-sm font-semibold hover:bg-[#8ea2c8] transition-colors duration-300"
              >
                Réserver
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#0B132B] text-white group-hover:rotate-45 transition-transform duration-300">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </button>

              <button
                data-testid="nav-menu-toggle"
                onClick={() => setOpen(true)}
                className="lg:hidden flex items-center justify-center w-11 h-11 rounded-full bg-white/10 border border-white/15 text-white"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#0B132B] grain lg:hidden"
          >
            <div className="relative z-10 flex flex-col h-full px-6 py-6">
              <div className="flex items-center justify-between">
                <Logo onClick={() => { setOpen(false); scrollTo("top"); }} />
                <button
                  data-testid="mobile-menu-close"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center w-11 h-11 rounded-full bg-white/10 border border-white/15 text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <nav className="flex-1 flex flex-col justify-center gap-2">
                {links.map((l, i) => (
                  <motion.button
                    key={l.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.08 }}
                    onClick={() => { setOpen(false); scrollTo(l.id); }}
                    className="group flex items-center justify-between border-b border-white/10 py-5 text-left"
                  >
                    <span className="font-display text-4xl text-white group-hover:text-[#8ea2c8] transition-colors">{l.label}</span>
                    <ArrowUpRight className="w-6 h-6 text-white/40 group-hover:text-white transition-colors" />
                  </motion.button>
                ))}
              </nav>

              <div className="flex flex-col gap-3">
                <a href={`tel:${CONTACT.phoneIntl}`} className="flex items-center justify-center gap-2 bg-white text-[#0B132B] py-4 rounded-full font-semibold">
                  <Phone className="w-4 h-4" /> {CONTACT.phoneDisplay}
                </a>
                <button onClick={() => { setOpen(false); scrollTo("booking"); }} className="border border-white/20 text-white py-4 rounded-full font-semibold">
                  Réserver en ligne
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
